/**
 * fetchLatestMetadata
 * -------------------
 * Returns the **newest** metadata JSON for the given user ID.
 *
 * Algorithm
 * • List all objects under   `user_metadata/{uid}/`.
 * • Filenames begin with an ISO timestamp → lexical sort gives newest.
 * • Download the newest object, parse JSON, and return `responses`
 *   (fallback to raw JSON for legacy files).
 *
 * @param  {string} uid  Firebase Auth UID
 * @returns {Promise<object|null>}  Last saved responses or `null` when none
 * @throws {Error}  network / permission issues
 */

import { getDownloadURL, getStorage, listAll, ref } from "firebase/storage";
import { firebaseApp } from "../../firebase/firebaseApp";

/* ------------------------------------------------------------------ */
/* Helper – fetch newest metadata JSON                                */
/* ------------------------------------------------------------------ */
export async function fetchLatestMetadata(uid) {
  const storage = getStorage(firebaseApp);
  const dirRef = ref(storage, `user_metadata/${uid}`);

  /* List all files in the user directory --------------------------- */
  const { items } = await listAll(dirRef);
  if (!items.length) return null; // no prior uploads

  /* Newest file = lexicographically last --------------------------- */
  items.sort((a, b) => a.name.localeCompare(b.name));
  const newestRef = items[items.length - 1];

  /* Download + parse ---------------------------------------------- */
  const url = await getDownloadURL(newestRef);
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");

  const json = await res.json(); // { responses, … }
  return json.responses ?? json; // legacy fallback
}
